import Box from '@material-ui/core/Box';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Switch from '@material-ui/core/Switch';
import AddressField from 'fhir/general/Address/Address';
import {
  CARE_FACILITY_SUB_TYPE,
  CONTACT_PERSON_PRIMARY,
  CONTACT_PERSON_STATUS,
  CREATE_FACILITY_TEXT,
  HOME_HEALTH_CODE,
  ORGNAIZATION_SUB_TYPE,
} from 'master-list/constants';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TextField, { INPUT_PATTERN_NUMBER, MAX_LENGTH_NPI } from 'styleguide/form/Text';
import ValueSet from 'styleguide/form/ValueSet';
import FieldSet from 'styleguide/layout/FieldSet';
import { ICareFacility } from '../../care-facility';
import { isContactPrimary } from '../utils';
import MultipleContactInfo from './MultipleContactInfo';
import MultipleContactPerson from './MultipleContactPerson';
import LookupFilterLayout from 'styleguide/layout/LookupFilter';
import { NPI_LABEL } from 'pcp/constants';
import { useLazyQuery } from '@apollo/client';
import { CareFacilityByNPIQuery } from '../Schema';
import Loading from 'styleguide/Loading';
import Alert from 'styleguide/layout/Alert';
import Typography from '@material-ui/core/Typography';
import { useLDFlagValue } from 'common/useLDFlagValue';
import { HOME_HEALTH_V2_FLAG } from 'common/constants';

type FormType = 'create' | 'update';

type Props = {
  data: ICareFacility;
  onChange: (data) => void;
  onValidate: (isValid: boolean) => void;
  formFields: any;
  entities?: fhir.Organization[];
  formType?: FormType;
};

const Form: React.FC<Props> = ({
  data,
  onChange,
  onValidate,
  formFields,
  entities,
  formType = 'create'
}) => {
  const [validity, setValidity] = useState<any>({});
  const { organization } = useMemo(
    () => ({ organization: data?.organization }),
    [data]
  );
  const type = organization?.type?.find(
    (type) => type?.coding?.[0]?.system === ORGNAIZATION_SUB_TYPE
  );
  const isHomeHealthV2FlagEnabled = useLDFlagValue(
    HOME_HEALTH_V2_FLAG
  );
  const organizationType = type?.coding?.[0]?.code;

  const [npiNumber, setNpiNumber] = useState<string>();
  const [isNpiData, setIsNpiData] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const handleAddressChange = useCallback(
    (address: fhir.Address | undefined) => {
      onChange({
        ...(data || {}),
        organization: {
          ...organization,
          address: address ? [address] : undefined,
        },
      });
    },
    [data, onChange, organization]
  );

  const handleNameChange = useCallback(
    (name: string | undefined) => {
      onChange({
        ...(data || {}),
        organization: {
          ...organization,
          name,
        },
      });
    },
    [data, onChange, organization]
  );

  const handleTypeChange = useCallback(
    (type: fhir.CodeableConcept) => {
      const filteredType =
        organization?.type?.filter(
          (type) => type?.coding?.[0]?.system !== ORGNAIZATION_SUB_TYPE
        ) || [];
      onChange({
        ...(data || {}),
        organization: {
          ...organization,
          type: [...filteredType, type],
        },
      });
    },
    [data, onChange, organization]
  );

  const handleStatusChange = useCallback(
    (value: boolean) => {
      onChange({
        ...(data || {}),
        organization: {
          ...organization,
          active: value,
        },
      });
    },
    [data, onChange, organization]
  );

  const handleContactInfoChange = useCallback(
    (contacts) => {
      onChange({
        ...(data || {}),
        organization: {
          ...organization,
          telecom: contacts,
        },
      });
    },
    [data, onChange, organization]
  );

  const handleContactPersonChange =
    (selectedIndex?: any | undefined) => (value: any) => {
      if (selectedIndex === undefined) {
        let lastItem = value[value?.length - 1];
        if (lastItem && lastItem?.extension === undefined) {
          lastItem = {
            ...lastItem,
            extension: [
              { url: CONTACT_PERSON_STATUS, valueBoolean: true },
              {
                url: CONTACT_PERSON_PRIMARY,
                valueBoolean: value?.length === 1 ? true : false,
              },
            ],
          };
        }
        const updatedContacts = lastItem
          ? [...value?.slice(0, -1), lastItem]
          : value;
        onChange({
          ...(data || {}),
          practitioners: updatedContacts,
        });
      } else {
        let items = data?.practitioners || [];
        const isSelectedPrimary = isContactPrimary(value);
        if (isSelectedPrimary) {
          const updatedContact = items?.map((item) => {
            const extension =
              item?.extension?.filter(
                (item) => item?.url !== CONTACT_PERSON_PRIMARY
              ) || [];
            return {
              ...item,
              extension: [
                ...extension,
                { url: CONTACT_PERSON_PRIMARY, valueBoolean: false },
              ],
            };
          });
          items = updatedContact.map((item, itemIndex) =>
            itemIndex === selectedIndex ? value : item
          );
        } else {
          items = items.map((item, itemIndex) =>
            itemIndex === selectedIndex ? value : item
          );
        }
        onChange({
          ...(data || {}),
          practitioners: items,
        });
      }
    };

  const handleValidityChange = useCallback(
    (key: string) => (isValid: boolean | undefined) => {
      if (validity[key] !== isValid) {
        setValidity({
          ...validity,
          [key]: isValid,
        });
      }
    },
    [validity]
  );

  useEffect(() => {
    const isValid = Object.keys(validity).every((key: string) => validity[key]);
    onValidate(isValid);
  }, [validity, onValidate]);

  const generateFhirAddressFromNPIAddressData = (addressData: any) => {
    return {
      line: [addressData?.address],
      city: addressData?.city,
      state: addressData?.state,
      postalCode: addressData?.postalCode,
    }
  }

  const [
    getHealthCareFacilityByNPI,
    { loading: healthCareFacilityLoading },
  ] = useLazyQuery(CareFacilityByNPIQuery, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const npiData = data?.getHealthCareFacilityByNPI;
      const hasTelecomData = npiData?.locationAddress?.fax ?? npiData?.locationAddress?.phone;
      const { telecom: _, ...organizationWithoutTelecom } = organization;
      
      onChange({
        ...(data ?? {}),
        organization: {
          ...(hasTelecomData ? organization : organizationWithoutTelecom),
          active: true,
          name: npiData?.organizationName + ' - ' + npiData?.locationAddress?.city,
          address: generateFhirAddressFromNPIAddressData(npiData?.locationAddress) ? [generateFhirAddressFromNPIAddressData(npiData?.locationAddress)] : undefined,
          identifier: [ {
            use: 'official',
            system: 'http://hl7.org/fhir/sid/us-npi', // NOSONAR
            value: npiNumber
          } ],
          ...(hasTelecomData ? {
            telecom: [{
              telecom: [
                ...(npiData?.locationAddress?.fax ? [{
                  system: "fax",
                  value: npiData?.locationAddress?.fax,
                  rank: 1
                }] : []),
                ...(npiData?.locationAddress?.phone ? [{
                  system: "phone",
                  value: npiData?.locationAddress?.phone,
                  use: "work",
                  rank: 1
                }] : [])
              ]
            }]
          } : {}),
        },
      });
      setIsNpiData(true)
    },
    onError: (error) => {
      const errorMsg = error?.message?.replace('GraphQL error: ', '') || 'Failed to fetch NPI data. Please try again.';
      setErrorMessage(errorMsg);
      setIsNpiData(false);
    },
  });

  const applyFilters = () => {
    getHealthCareFacilityByNPI({
      variables: {
        npiNumber: npiNumber,
      },
    });
    setIsNpiData(false);
  };

  const renderFormFields = (isFromNpiData = false) => (
    <>
      {(isFromNpiData && formType === 'update') && <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              name="display"
              onChange={handleNameChange}
              onValidate={handleValidityChange('facilityName')}
              required
              label={'NPI'}
              value={organization?.identifier?.[0]?.value}
              disabled={isFromNpiData}
            />
          </Grid>
      </Grid>}
      <Box mt={2} />
      {formFields?.nameLabel && (
        <FieldSet
          label={formFields?.nameLabel}
          headerContent={
            <Box display="flex" justifyContent="flex-end" width={1}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={organization?.active}
                    onChange={(event) =>
                      handleStatusChange(event?.target?.checked)
                    }
                    color="primary"
                    value={organization?.active}
                  />
                }
                label="Active"
                labelPlacement="end"
              />
            </Box>
          }
          required
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="display"
                onChange={handleNameChange}
                onValidate={handleValidityChange('facilityName')}
                required
                label={'Name'}
                value={organization?.name}
                disabled={isFromNpiData}
              />
            </Grid>
          </Grid>
        </FieldSet>
      )}
      <Box mt={2} />
      {formFields?.typeLabel && (
        <FieldSet label={formFields?.typeLabel} required>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ValueSet
                name="type"
                value={type?.coding?.[0]?.code}
                onChange={handleTypeChange}
                onValidate={handleValidityChange('type')}
                getOptionWidth={(option, id, label, isMobile) =>
                  isMobile ? 12 : 6
                }
                variant="radio-button-group"
                required
                url={CARE_FACILITY_SUB_TYPE}
              />
            </Grid>
          </Grid>
        </FieldSet>
      )}
      <Box mt={2} />
      {formFields?.addressLabel && (
        <FieldSet label={formFields?.addressLabel}>
          <AddressField
            disabled={isFromNpiData}
            data={organization?.address?.[0]}
            onChange={handleAddressChange}
            onValidate={handleValidityChange('facilityAddress')}
            isStateRequired
            entities={
              formFields?.viewHeaderTitle === CREATE_FACILITY_TEXT
                ? entities
                : undefined
            }
            showInstruction={formFields?.showAddressInstruction}
            showCounty={true}
            showMarket={true}
            careFacility={
              formFields?.viewHeaderTitle === CREATE_FACILITY_TEXT
                ? true
                : false
            }
          />
        </FieldSet>
      )}
      <Box mt={2} />
      {formFields?.contactInfoLabel && (
        <MultipleContactInfo
          data={organization?.telecom}
          onChange={handleContactInfoChange}
          onValidate={handleValidityChange('contactInfo')}
          title={formFields?.contactInfoLabel}
          {...((isHomeHealthV2FlagEnabled && organizationType === HOME_HEALTH_CODE ) && { isRequired: true })}
        />
      )}
      <Box mt={2} />
      {formFields?.contactPersonLabel && (
        <MultipleContactPerson
          data={data?.practitioners}
          onChange={handleContactPersonChange}
          onValidate={handleValidityChange('contactPerson')}
          title={formFields?.contactPersonLabel}
        />
      )}
    </>
  );

  return (
    <>
      {healthCareFacilityLoading && (
          <Loading variant='spinner' />
      )}
      {(isHomeHealthV2FlagEnabled && organizationType === HOME_HEALTH_CODE && formType === 'create') ? (
        <>
          <LookupFilterLayout
            onApply={() => applyFilters()}
            disableSearchButton={!npiNumber}
            onReset={() => {
              setNpiNumber("");
              setIsNpiData(false);
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={NPI_LABEL}
                  name="npi"
                  required
                  value={npiNumber}
                  onChange={(value: string) => {
                    setNpiNumber(value);
                  }}
                  onValidate={() => {}}
                  inputPattern={INPUT_PATTERN_NUMBER}
                  maxLength={MAX_LENGTH_NPI}
                />
              </Grid>
            </Grid>
          </LookupFilterLayout>
          {isNpiData && renderFormFields(true)}
        </>
      ) : (
        renderFormFields(organizationType === HOME_HEALTH_CODE && isHomeHealthV2FlagEnabled)
      )}
      <Alert
        open={!!errorMessage}
        icon="report_problem_outline_outline"
        title="Error"
        submitButtonText="CLOSE"
        hideCancelButton
        onSubmit={() => setErrorMessage(undefined)}
      >
        <Typography color="textSecondary">{errorMessage}</Typography>
      </Alert>
    </>
  );
};

export default Form;
